import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { Icone } from './Icone';
import { NomeDaAmostra, daApi } from './NomeDaAmostra';
import { verFicha } from './api/ficha';
import type { DadosDaFicha } from './api/ficha';
import { Dica } from './telas/AdensamentoGraficos';
import { clienteEObra, data, faixaProfundidade, rotulo } from './formato';
import { ABRIR_PENDENCIAS } from './Pendencias';
import type { PendenciaDetalhada } from './Pendencias';
import {
  AcoesDoLaudo, Area, Aviso, Dialogo, EscolhaPorCodigo, Selo,
  SeloDoEnsaio, useEstreito,
} from './ui';

/**
 * As peças comuns das fichas de ensaio no Carbon (onda 236).
 *
 * «Padronize as fichas de lançamento e interpretação de ensaios no
 *  padrão Carbon» — Humberto, 02/10/2026, depois de aprovar as pranchetas
 * («Fichas de ensaio · Carbon»). Toda ficha passa a ter a mesma espinha,
 * de cima para baixo:
 *
 *   1. o CABEÇALHO — sigla e norma vigente, o nome do ensaio, o estado, e
 *      a identificação (OS, cliente e obra, amostra, origem, execução);
 *   2. a FAIXA DE RESULTADO — os números que valem, sempre à vista, com
 *      o «i» de como se obtêm e a célula do que falta para o laudo;
 *   3. as ETAPAS — uma por tela, como no adensamento (187), com a etapa
 *      na URL para o F5 e o link de pendência não perderem o lugar;
 *   4. os BLOCOS da etapa, e o rodapé que leva à etapa vizinha.
 *
 * As peças moram aqui, e não em cada tela, pela razão de sempre: catorze
 * fichas com a mesma faixa escrita catorze vezes são catorze lugares para
 * a mesma correção ser feita em treze.
 *
 * A trilha (OS › amostra › ensaio) NÃO está aqui: a moldura já a resolve
 * pelo endereço (182), e uma segunda trilha seria a segunda versão da
 * mesma verdade.
 */

// ── os estados do ensaio, com tom ────────────────────────────────────

// 267: o tom da situação do ensaio mora no selo único (ui.tsx).

/** Aprovado, cancelado ou substituído: a ficha só se lê (UX-049). */
export const fichaCongelada = (status?: string | null) =>
  ['aprovado', 'cancelado', 'substituido'].includes(status ?? '');

// ── a identificação ──────────────────────────────────────────────────

/**
 * `versao` muda a cada gravação: gravar a terceira cápsula pode levar o
 * ensaio de «em execução» a «dados completos» (0118), e o selo do topo
 * tem que acompanhar.
 */
export function useFicha(id: string, norma?: string, versao?: unknown) {
  const [f, setF] = useState<DadosDaFicha | null>(null);
  useEffect(() => {
    let vivo = true;
    void verFicha(id, norma).then((r) => { if (vivo) setF(r); })
      .catch(() => { /* o cabeçalho cai para o nome da tela */ });
    return () => { vivo = false; };
  }, [id, norma, versao]);
  return f;
}

/**
 * O topo da ficha.
 *
 * A norma que aparece é a VIGENTE no catálogo para o código pedido — e
 * não a escrita à mão na tela, que era como as fichas diziam «NBR 6459:
 * 2016» meses depois de a Testesolo ter cadastrado a de 2025.
 */
export function CabecalhoDaFicha({
  id, sigla, titulo, ficha, normaDaTela, tentativaAberta,
}: {
  id: string;
  /**
   * A repetição aberta (onda 323, RN-260): o cabeçalho desenha o selo
   * «Repetindo · tentativa N» ao lado da situação — um desenho só para as
   * sete fichas (P-UI-41). `null`/ausente: sem repetição, sem selo.
   */
  tentativaAberta?: number | null;
  sigla: string;
  titulo: string;
  ficha: DadosDaFicha | null;
  /** O que mostrar enquanto a identificação não chega. */
  normaDaTela: string;
}) {
  const f = ficha;
  const quemObra = clienteEObra(f?.cliente, f?.obra_nome).join(' · ');
  const origem = [f?.ponto_investigacao,
    faixaProfundidade(f?.profundidade_inicial, f?.profundidade_final)
      ? `${faixaProfundidade(f?.profundidade_inicial, f?.profundidade_final)} m`
      : null].filter(Boolean).join(' · ');
  const execucao = [f?.executado_por_nome, data(f?.data_execucao)]
    .filter(Boolean).join(' · ');
  const osEObra = [f?.os_numero ? `OS ${f.os_numero}` : null, quemObra || null]
    .filter(Boolean).join(' · ');
  return (
    <header className="fe-cabecalho">
      <div className="fe-titulo">
        <div>
          <p className="fe-metodo">
            <span className="fe-sigla">{sigla}</span>
            <span>{f?.norma ?? normaDaTela}</span>
          </p>
          <h1>{titulo}</h1>
        </div>
        <div className="fe-cab-acoes">
          {tentativaAberta != null && (
            <Selo tom="atencao">Repetindo · tentativa {tentativaAberta}</Selo>
          )}
          {f?.status && (
            <SeloDoEnsaio status={f.status} />
          )}
          <Link className="botao discreto" to={`/ensaio/${id}/situacao`}>
            Situação e versões
          </Link>
          <Link className="botao discreto" to="/fila">Fila</Link>
          {/* 310: no celular os dois botões acima saem e moram no ⋯. */}
          <MenuDoEnsaio id={id} ficha={f} />
        </div>
      </div>
      {/* 310 · a identificação no celular, em duas linhas curtas: a amostra
          pelo nome do cliente (303) e a origem; a OS, o cliente e a obra. A
          grade de cinco caixas abaixo fica para o desktop — quem escolhe é o
          CSS (`celular.css`, seção 310). Sem link aqui: um link de 20 px no
          meio da linha não é alvo de dedo (RN-251 c) — a OS e a amostra se
          abrem pelo ⋯. */}
      <p className="fe-ident-curta">
        <span className="fe-ident-linha">
          {f?.amostra_codigo
            ? <NomeDaAmostra emLinha amostra={daApi(f)} />
            : <span>—</span>}
          {origem && <span className="fe-ident-aux"> · {origem}</span>}
        </span>
        {osEObra && <span className="fe-ident-linha fe-ident-aux">{osEObra}</span>}
      </p>
      <dl className="fe-ident">
        <div><dt>OS</dt><dd className="cod">
          {f?.os_id ? <Link to={`/os/${f.os_id}`}>{f.os_numero}</Link> : '—'}
        </dd></div>
        <div><dt>Cliente · obra</dt><dd title={quemObra}>{quemObra || '—'}</dd></div>
        <div><dt>Amostra</dt><dd>
          {f?.amostra_codigo
            /* Onda 303 (RN-248): o nome do cliente em destaque, o código
               logo depois; o link continua pelo código. */
            ? <><Link to={`/amostra/${f.amostra_codigo}`}>
              <NomeDaAmostra emLinha amostra={daApi(f)} /></Link>
              {f.tipo_amostra ? ` · ${rotulo(f.tipo_amostra).toLowerCase()}` : ''}</>
            : '—'}
        </dd></div>
        <div><dt>Origem</dt><dd title={origem}>{origem || '—'}</dd></div>
        <div><dt>Execução</dt><dd title={execucao}>{execucao || '—'}</dd></div>
      </dl>
    </header>
  );
}

/**
 * O menu «⋯» do cabeçalho da ficha, no celular (310, spec 06 §7.1).
 *
 * No celular a linha do título tem lugar para a sigla, o nome e o selo — e
 * não para «Situação e versões» e «Fila» ao lado. Os dois vêm para cá, com
 * a OS e a amostra (que no desktop se abrem pelos links da grade). Fecha
 * por fora e por Esc, o mesmo jeito do `MenuDaOs` (`OsMoldura.tsx`).
 *
 * O botão só aparece abaixo de 640 px — quem decide é o CSS; acima disso
 * o `display: none` do `celular.css` o tira, e os dois botões de sempre
 * continuam no lugar.
 */
function MenuDoEnsaio({ id, ficha: f }: { id: string; ficha: DadosDaFicha | null }) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLSpanElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return undefined;
    /* Quem abre pelo teclado continua no menu: o foco vai ao primeiro item. */
    lista.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setAberto(false);
      botao.current?.focus({ preventScroll: true });
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);
  return (
    <span className="fe-menu-caixa" ref={raiz}>
      <button type="button" className="fe-menu" ref={botao}
        aria-haspopup="menu" aria-expanded={aberto}
        aria-label="Mais ações do ensaio" title="Mais ações do ensaio"
        onClick={() => setAberto((a) => !a)}>
        <Icone nome="pontos" />
      </button>
      {aberto && (
        <div className="fe-menu-itens" role="menu" aria-label="Mais ações do ensaio"
          ref={lista}
          onClick={(e) => {
            /* Escolher um item fecha o menu — o link segue. */
            if ((e.target as HTMLElement).closest('a')) setAberto(false);
          }}>
          <Link role="menuitem" to={`/ensaio/${id}/situacao`}>Situação e versões</Link>
          <Link role="menuitem" to="/fila">Fila</Link>
          {f?.os_id && <Link role="menuitem" to={`/os/${f.os_id}`}>Abrir a OS</Link>}
          {f?.amostra_codigo
            && <Link role="menuitem" to={`/amostra/${f.amostra_codigo}`}>Abrir a amostra</Link>}
        </div>
      )}
    </span>
  );
}

// ── a faixa de resultado ─────────────────────────────────────────────

export interface Indicador {
  rotulo: string;
  /** `null` = ainda não existe; a célula mostra o traço e o porquê. */
  valor: ReactNode | null;
  unidade?: string;
  /** O número que o ensaio existe para dar — leva o fio de cima. */
  chave?: boolean;
  /** Como se obtém — abre no «i». */
  como?: ReactNode;
  /** Nota curta embaixo do número («de 3 mín.», «fora de 15–35»). */
  nota?: ReactNode;
  /** Por que está vazio, quando está. */
  porque?: string;
}

/**
 * Os números que valem, em linha, sempre à vista.
 *
 * Os valores vêm do SERVIDOR (D-29) e já arredondados como a norma do
 * ensaio manda — a faixa não arredonda nem recalcula nada.
 *
 * A última célula responde «posso mandar para verificação?»: completo, ou
 * quantos itens TRAVAM, com o caminho para a lista. Avisos que não travam
 * (0090) não entram na conta.
 */
export function FaixaDaFicha({ indicadores, pendencias, itens, faltaNaTela }: {
  indicadores: Indicador[];
  pendencias?: PendenciaDetalhada[];
  itens: string[];
  /** O que a própria ficha sabe que falta e o banco ainda não cobra como
   *  pendência (o triaxial sem envoltória, por exemplo): sem isso, a faixa
   *  diria «Completo para o laudo» de um ensaio sem resultado. */
  faltaNaTela?: string | null;
}) {
  const travam = pendencias
    ? pendencias.filter((p) => p.trava && !p.justificada).length
    : itens.length;
  const falta = !travam && faltaNaTela ? faltaNaTela : null;
  return (
    <dl className="fe-faixa" aria-label="Resultado do ensaio">
      {indicadores.map((i) => {
        const vazio = i.valor == null || i.valor === '';
        return (
          <div key={i.rotulo}
            className={`fe-ind${i.chave ? ' chave' : ''}${vazio ? ' vazio' : ''}`}>
            <dt>
              <span>{i.rotulo}</span>
              {i.como && <Dica rotulo={i.rotulo}>{i.como}</Dica>}
            </dt>
            <dd title={vazio ? i.porque : undefined}>
              {vazio ? '—' : i.valor}
              {!vazio && i.unidade && <span className="u">{i.unidade}</span>}
            </dd>
            {i.nota != null && <span className="nota">{i.nota}</span>}
          </div>
        );
      })}
      <div className={`fe-ind pend${travam || falta ? '' : ' ok'}`}>
        <dt className="so-leitor">Laudo</dt>
        <dd>
          <b>{travam ? `Faltam ${travam} para o laudo` : falta ?? 'Completo para o laudo'}</b>
          {/* 310: no celular a lista é uma linha fechada (`Pendencias`); o
              toque aqui a abre, além de levar até ela. No desktop o evento
              não tem quem o ouça e o link é o de sempre. */}
          <a href="#pendencias" className="fe-pend-link"
            onClick={() => window.dispatchEvent(new Event(ABRIR_PENDENCIAS))}>
            {travam ? 'ver o que falta' : 'ver a lista'}
          </a>
        </dd>
      </div>
    </dl>
  );
}

// ── as etapas ────────────────────────────────────────────────────────

export interface Etapa {
  nome: string;
  /** O resumo do que já está lá («3 válidas · 1 descartada»). */
  resumo?: string;
  feita: boolean;
  /** Tem pendência que trava — o ícone vira alerta. */
  falta?: boolean;
}

/**
 * A etapa vem da URL (`?etapa=`), como no adensamento (187): o link de
 * uma pendência abre na etapa certa e o F5 não perde o lugar. Sem nada na
 * URL, abre a primeira que ainda tem trabalho.
 */
export function useEtapa(total: number, sugerida: number) {
  const [params, setParams] = useSearchParams();
  const pedida = Number(params.get('etapa'));
  const etapa = pedida >= 1 && pedida <= total ? pedida : sugerida;
  const irPara = (n: number) => {
    const q = new URLSearchParams(params);
    q.set('etapa', String(n));
    setParams(q, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return [etapa, irPara] as const;
}

function IconeDaEtapa({ estado }: { estado: 'feito' | 'atual' | 'falta' | '' }) {
  return (
    <svg className="fe-ic" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" />
      {estado === 'feito' && <path d="M4.8 8.2l2.1 2.1 4.3-4.6" />}
      {estado === 'atual' && <path className="cheio" d="M8 1a7 7 0 0 1 0 14z" />}
      {estado === 'falta' && <path d="M8 4.2v4.6M8 10.8v.9" />}
    </svg>
  );
}

/**
 * As etapas da ficha.
 *
 * No celular (310, spec 06 §7.4) a régua de quatro a seis colunas não cabe
 * em 412 px — os nomes quebravam em três linhas e empurravam o trabalho
 * para baixo. Vira uma linha só, «‹ **Pesagem úmida** · 2 de 4 ›», com a
 * régua de segmentos embaixo; tocar no nome mostra a lista inteira (o
 * mesmo `ol` do desktop, com a classe `aberta`). Acima de 640 px nada muda.
 */
export function EtapasDaFicha({ etapas, atual, irPara }: {
  etapas: Etapa[]; atual: number; irPara(n: number): void;
}) {
  const estreito = useEstreito(640);
  const [lista, setLista] = useState(false);
  const idDaLista = useId();
  const estadoDa = (e: Etapa, n: number) =>
    (n === atual ? 'atual' : e.falta ? 'falta' : e.feita ? 'feito' : '');
  const ir = (n: number) => { setLista(false); irPara(n); };
  const corrente = etapas[atual - 1];
  return (
    <>
      {estreito && corrente && (
        <div className="fe-etapas-celular">
          <button type="button" aria-label="Etapa anterior"
            disabled={atual <= 1} onClick={() => ir(atual - 1)}>
            <Icone nome="esquerda" />
          </button>
          <button type="button" className="fe-etapa-atual"
            aria-expanded={lista} aria-controls={idDaLista}
            onClick={() => setLista((v) => !v)}>
            <b>{corrente.nome}</b>
            <span className="fe-etapa-conta"> · {atual} de {etapas.length}</span>
          </button>
          <button type="button" aria-label="Próxima etapa"
            disabled={atual >= etapas.length} onClick={() => ir(atual + 1)}>
            <Icone nome="direita" />
          </button>
          {/* A régua: um traço por etapa. A cor não é a única pista — o nome
              e o «2 de 4» estão escritos logo acima. */}
          <div className="fe-segmentos" aria-hidden="true">
            {etapas.map((e, k) => (
              <span key={e.nome} className={estadoDa(e, k + 1) || undefined} />
            ))}
          </div>
        </div>
      )}
      <ol id={idDaLista}
        className={`fe-etapas${estreito && lista ? ' aberta' : ''}`}
        aria-label="Etapas do ensaio">
        {etapas.map((e, k) => {
          const n = k + 1;
          const estado = estadoDa(e, n);
          return (
            <li key={e.nome} className={estado || undefined}>
              <button type="button" onClick={() => ir(n)}
                aria-current={n === atual ? 'step' : undefined}>
                <IconeDaEtapa estado={estado} />
                <span className="nome">
                  {n} · {e.nome}
                  {e.resumo && <span className="resumo">{e.resumo}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </>
  );
}

export function RodapeDeEtapas({ etapas, atual, irPara, nota }: {
  etapas: Etapa[]; atual: number; irPara(n: number): void; nota?: ReactNode;
}) {
  return (
    <div className="fe-rodape-etapas">
      {atual > 1
        ? <button type="button" className="botao secundario"
            onClick={() => irPara(atual - 1)}>← {etapas[atual - 2].nome}</button>
        : <span />}
      <span className="meio">{nota}</span>
      {atual < etapas.length
        ? <button type="button" className="principal grande"
            onClick={() => irPara(atual + 1)}>
            {etapas[atual].nome} <span aria-hidden="true">→</span>
          </button>
        : <span />}
    </div>
  );
}

// ── o bloco ──────────────────────────────────────────────────────────

export function BlocoDaFicha({ titulo, selo, sub, children, classe }: {
  titulo: ReactNode;
  selo?: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  classe?: string;
}) {
  return (
    <section className={`bloco fe-bloco ${classe ?? ''}`.trim()}>
      <header className="fe-bloco-topo">
        <h2><span>{titulo}</span></h2>
        {selo}
      </header>
      {sub && <p className="sub">{sub}</p>}
      {children}
    </section>
  );
}

// ── os equipamentos, e a balança ─────────────────────────────────────

export interface EquipamentoDaLista {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  categoria: string;
  apto?: boolean | null;
  calibracao_ate?: string | null;
  afericao_ate?: string | null;
  impedimento?: string | null;
}

export function useEquipamentos() {
  const [equip, setEquip] = useState<EquipamentoDaLista[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const e = await api<EquipamentoDaLista[]>('/equipamentos?limite=500');
        setEquip(Array.isArray(e) ? e : []);
      } catch { /* o seletor cai para lista vazia, e a tela diz isso */ }
    })();
  }, []);
  return equip;
}

/**
 * A balança da bateria de pesagens (RN-215, onda 236).
 *
 * UMA escolha por bateria, e não uma por cápsula: «para triplicatas de
 * umidade deve escolher apenas 1 vez a balança (pois obrigatoriamente
 * será a mesma balança)» — Humberto, 02/10/2026. Vai gravada com as
 * determinações; o servidor a registra no ensaio (rastreabilidade do
 * equipamento, RN-151) e recusa o que não é balança ou está impedido.
 *
 * O selo embaixo diz, antes de gravar, se ela pode entrar no ensaio —
 * quem escolhe a balança vencida descobre na hora, e não na recusa.
 */
export function EscolhaDeBalanca({
  rotulo: r = 'Balança', equipamentos, valor, aoEscolher, erro, desabilitado,
}: {
  rotulo?: string;
  equipamentos: EquipamentoDaLista[];
  valor: string;
  aoEscolher(v: string): void;
  erro?: string;
  desabilitado?: boolean;
}) {
  const balancas = useMemo(
    () => equipamentos.filter((e) => e.categoria === 'balanca'), [equipamentos]);
  const escolhida = balancas.find((b) => b.id === valor);
  const validade = escolhida?.calibracao_ate ?? escolhida?.afericao_ate ?? null;
  return (
    /* A balança é do bloco, não célula de linha: o Enter/Tab das tabelas
       de lançamento não passa por ela (onda 354 · o «Pesar» da Estufa). */
    <div className="fe-balanca" data-teclado-fora="">
      <EscolhaPorCodigo
        rotulo={r}
        opcoes={balancas.map((b) => ({
          valor: b.id, codigo: b.codigo_patrimonial, detalhe: b.nome }))}
        valor={valor}
        aoEscolher={aoEscolher}
        erro={erro}
        desabilitado={desabilitado}
      />
      {escolhida && (
        <p className="fe-balanca-estado">
          {escolhida.impedimento
            ? <Selo tom="critico">{escolhida.impedimento}</Selo>
            : <Selo tom="ok">Pode ser usada</Selo>}
          {validade && !escolhida.impedimento
            && <span className="sub"> até {data(validade)}</span>}
        </p>
      )}
      {!escolhida && balancas.length === 0 && (
        <p className="sub">Nenhuma balança cadastrada — cadastre em Equipamentos.</p>
      )}
    </div>
  );
}

// ── o descarte com motivo ────────────────────────────────────────────

/**
 * Descartar uma linha lançada: com motivo, numa janela, e não num campo
 * aberto em cada linha da tabela.
 *
 * O campo por linha era uma segunda coluna de digitação no meio da
 * bateria — o técnico tabulava para dentro dele achando que era o próximo
 * número. A regra não muda (restrição 25 — nada se apaga; o motivo tem
 * pelo menos 5 caracteres e sai no laudo).
 */
export function JanelaDeDescarte({ alvo, aoFechar, aoConfirmar, salvando }: {
  /** O que vai ser descartado, por extenso («determinação 2 · CAP-07»). */
  alvo: string | null;
  aoFechar(): void;
  aoConfirmar(motivo: string): Promise<boolean>;
  salvando: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  useEffect(() => { if (alvo) setMotivo(''); }, [alvo]);
  const curto = motivo.trim().length < 5;
  return (
    <Dialogo aberto={alvo !== null} titulo={`Descartar ${alvo ?? ''}`}
      aoFechar={aoFechar}>
      <p className="sub">
        A linha continua no ensaio, riscada, com o motivo — e o laudo diz
        que ela foi descartada. Nada se apaga.
      </p>
      <Area rotulo="Motivo do descarte" rows={3} value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica="Pelo menos 5 caracteres — o que aconteceu na bancada." />
      <div className="acoes">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao perigo"
          aria-disabled={curto || salvando}
          title={curto ? 'Escreva o motivo — pelo menos 5 caracteres.' : undefined}
          onClick={() => {
            if (curto || salvando) return;
            void aoConfirmar(motivo.trim()).then((ok) => { if (ok) aoFechar(); });
          }}>
          {salvando ? 'Descartando…' : 'Descartar'}
        </button>
      </div>
    </Dialogo>
  );
}

// ── a lista do que falta ─────────────────────────────────────────────

/** A âncora da célula «Faltam N» da faixa. */
export function AncoraDePendencias({ children }: { children: ReactNode }) {
  return <div id="pendencias" className="fe-pendencias">{children}</div>;
}

// ── a última etapa: fotos e laudo ────────────────────────────────────

export function LaudoDaFicha({ id, nota }: { id: string; nota?: string }) {
  return (
    <BlocoDaFicha titulo="Laudo"
      sub="O PDF sai com marca d'água PRELIMINAR até haver relatório aprovado que o ampare (PD-85).">
      <div className="fe-miolo">
        {nota && <Aviso tipo="info">{nota}</Aviso>}
        <AcoesDoLaudo ensaioId={id} />
      </div>
    </BlocoDaFicha>
  );
}
